import { useEffect, useState, useCallback } from "react";
import { Combobox, Listbox, Icon, LegacyStack, Tag } from "@shopify/polaris";
import { SearchIcon } from "@shopify/polaris-icons";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import _ from "lodash";
import { fetch, fetchById } from "services/user-select.service";

interface Option {
  label: string;
  value: string;
}

interface UserSelectProps {
  value?: string[];
  onChange: (value: string[]) => void;
  isRequired?: boolean;
  isDisable?: boolean;
  label?: string;
  excludedUserIds?: string[];
  /** When false, API returns only non-guest users; when true, only guests; omit for all */
  isUserGuest?: boolean | string | undefined;
  /** When false, API excludes authorized/trusted persons (private); when true, only them; omit for all */
  isPrivate?: boolean | string | undefined;
  /** When true, show each user's company position alongside their name */
  showPosition?: boolean;
  readOnly?: boolean;
  /**
   * User ids that are already selected and must not be removable — e.g.
   * signers already invited on a template-generated document, where the
   * owner's rule allows adding signers but never removing an existing one
   * (removal only happens by withdrawing the whole document). New picks are
   * unaffected.
   */
  lockedValueIds?: string[];
}

const DEFAULT_EXCLUDED_USER_IDS: string[] = [];

const UserSelect: React.FC<UserSelectProps> = ({
  value = [],
  onChange,
  isRequired = false,
  isDisable = false,
  label = "select_user",
  excludedUserIds = DEFAULT_EXCLUDED_USER_IDS,
  isUserGuest = false,
  isPrivate = undefined,
  showPosition = false,
  readOnly = false,
  lockedValueIds = DEFAULT_EXCLUDED_USER_IDS,
}) => {
  const { t } = useTranslation();
  const [rawOptions, setRawOptions] = useState<Option[]>([]);
  const [options, setOptions] = useState<Option[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  const [labelCache, setLabelCache] = useState<Record<string, string>>({});
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const { companyId } = useParams<{ companyId: string }>();

  const valueIds = Array.isArray(value) ? value : [];
  const valueKey = valueIds.join(",");

  const formatUserLabel = useCallback(
    (nameOrEmail: string, position?: string | null) =>
      showPosition && position ? `${nameOrEmail} — ${position}` : nameOrEmail,
    [showPosition]
  );

  const fetchOptions = useCallback(
    async (searchText: string) => {
      setLoading(true);
      try {
        const data = await fetch(
          companyId || "",
          { textSearch: searchText },
          isUserGuest,
          isPrivate
        );
        const dataCollection = _.map(data.usersCollection, (row) => ({
          label: formatUserLabel(row.name || row.email, row.position),
          value: row.id,
        }));

        setRawOptions(dataCollection);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    },
    [companyId, isUserGuest, isPrivate, formatUserLabel]
  );

  // Sync UI selection from controlled value + known labels (no network).
  useEffect(() => {
    if (valueIds.length === 0) {
      setSelectedOptions((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    const listById = new Map(rawOptions.map((opt) => [opt.value, opt] as const));
    const cacheUpdates: Record<string, string> = {};

    const rememberLabel = (id: string, labelText: string) => {
      if (!labelText || labelText === id) return;
      if ((labelCache[id] ?? cacheUpdates[id]) === labelText) return;
      cacheUpdates[id] = labelText;
    };

    for (const id of valueIds) {
      const fromList = listById.get(id);
      if (fromList) rememberLabel(id, fromList.label);

      const selected = selectedOptions.find((opt) => opt.value === id);
      if (selected) rememberLabel(id, selected.label);
    }

    if (Object.keys(cacheUpdates).length > 0) {
      setLabelCache((prev) => ({ ...prev, ...cacheUpdates }));
    }

    const effectiveCache = { ...labelCache, ...cacheUpdates };

    setSelectedOptions((prev) => {
      const prevById = new Map(prev.map((opt) => [opt.value, opt]));
      const next = valueIds.map((id) => {
        const fromList = listById.get(id);
        if (fromList) return fromList;
        if (effectiveCache[id]) return { value: id, label: effectiveCache[id] };
        const fromPrev = prevById.get(id);
        if (fromPrev) return fromPrev;
        return { value: id, label: id };
      });

      if (
        next.length === prev.length &&
        next.every(
          (opt, i) =>
            opt.value === prev[i]?.value && opt.label === prev[i]?.label
        )
      ) {
        return prev;
      }
      return next;
    });
    // selectedOptions is read only to seed labelCache; omitted from deps to avoid loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueKey, rawOptions, labelCache]);

  // Fetch unknown labels. Depends on missing-id set only — search/rawOptions
  // updates must not abort in-flight fetchById calls.
  const missingLabelIdsKey = valueIds
    .filter((id) => !labelCache[id])
    .slice()
    .sort()
    .join(",");

  useEffect(() => {
    if (!missingLabelIdsKey) return;

    const idsNeedingFetch = missingLabelIdsKey.split(",");
    let cancelled = false;

    void (async () => {
      try {
        // Settle per-id so one deleted/failed user does not block labels for
        // the rest; cache failures as the id itself so missingLabelIdsKey
        // advances and the effect does not retry forever.
        const results = await Promise.allSettled(
          idsNeedingFetch.map((id) => fetchById(id, companyId))
        );
        if (cancelled) return;

        const fetchedEntries: Record<string, string> = {};
        results.forEach((result, index) => {
          if (result.status === "fulfilled") {
            const { userEntity } = result.value;
            fetchedEntries[userEntity.id] = formatUserLabel(
              userEntity.name || userEntity.email,
              userEntity.position
            );
            return;
          }
          const id = idsNeedingFetch[index];
          fetchedEntries[id] = id;
          console.error(result.reason);
        });

        if (Object.keys(fetchedEntries).length > 0) {
          setLabelCache((prev) => ({ ...prev, ...fetchedEntries }));
        }
      } catch (e) {
        console.error(e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [missingLabelIdsKey, companyId, formatUserLabel]);

  useEffect(() => {
    if (companyId) {
      fetchOptions("");
    }
  }, [companyId, fetchOptions]);

  useEffect(() => {
    const selectableRawOptions = rawOptions.filter(
      (user) => !excludedUserIds.includes(user.value)
    );

    if (selectedOptions.length > 0) {
      const selectedIds = new Set(selectedOptions.map((user) => user.value));
      const otherUsers = selectableRawOptions.filter(
        (user) => !selectedIds.has(user.value)
      );
      setOptions([...selectedOptions, ...otherUsers]);
    } else {
      setOptions(selectableRawOptions);
    }
  }, [selectedOptions, rawOptions, excludedUserIds]);

  const handleInputChange = (input: string) => {
    setInputValue(input);
    fetchOptions(input);
  };

  // Derive next selection from controlled `value`, not from possibly-partial
  // selectedOptions, so in-flight label fetches cannot drop pending ids.
  const handleSelection = (selected: string) => {
    if (excludedUserIds.includes(selected) || readOnly) return;

    const alreadySelected = valueIds.includes(selected);
    // Clicking an already-selected, locked option would otherwise deselect
    // (remove) it — a no-op here, same as handleTagRemove below.
    if (alreadySelected && lockedValueIds.includes(selected)) return;
    const nextIds = alreadySelected
      ? valueIds.filter((id) => id !== selected)
      : [...valueIds, selected];

    const found =
      options.find((opt) => opt.value === selected) ||
      rawOptions.find((opt) => opt.value === selected) ||
      selectedOptions.find((opt) => opt.value === selected) ||
      ({ value: selected, label: selected } as Option);

    if (!alreadySelected && found.label !== selected) {
      setLabelCache((cache) =>
        cache[selected] ? cache : { ...cache, [selected]: found.label }
      );
    }

    setSelectedOptions((prev) => {
      const prevById = new Map(prev.map((opt) => [opt.value, opt]));
      if (!alreadySelected) {
        prevById.set(selected, found);
      }
      return nextIds.map(
        (id) => prevById.get(id) || ({ value: id, label: id } as Option)
      );
    });

    onChange(nextIds);
  };

  const handleTagRemove = (tagToRemove: Option) => {
    if (readOnly || lockedValueIds.includes(tagToRemove.value)) return;
    const nextIds = valueIds.filter((id) => id !== tagToRemove.value);
    setSelectedOptions((prev) =>
      prev.filter((opt) => opt.value !== tagToRemove.value)
    );
    onChange(nextIds);
  };

  const optionsMarkup = options.map((option) => (
    <Listbox.Option
      disabled={readOnly || isDisable}
      key={option.value}
      value={option.value}
      selected={valueIds.includes(option.value)}
      accessibilityLabel={option.label}
    >
      {option.label}
    </Listbox.Option>
  ));

  const verticalContentMarkup = selectedOptions.map((option) =>
    lockedValueIds.includes(option.value) ? (
      // No onRemove — Polaris renders a plain (non-removable) tag.
      <Tag key={option.value}>{option.label}</Tag>
    ) : (
      <Tag key={option.value} onRemove={() => handleTagRemove(option)}>
        {option.label}
      </Tag>
    )
  );

  return (
    <div className="user-select">
      <Combobox
        allowMultiple
        activator={
          <Combobox.TextField
            readOnly={readOnly || isDisable}
            prefix={<Icon source={SearchIcon as any} />}
            onChange={handleInputChange}
            label={t(label)}
            value={inputValue}
            autoComplete="off"
            requiredIndicator={isRequired}
            error={
              isRequired && valueIds.length === 0 ? t("field_is_required") : ""
            }
            verticalContent={
              <LegacyStack spacing="extraTight" alignment="center">
                {verticalContentMarkup}
              </LegacyStack>
            }
          />
        }
      >
        <Listbox onSelect={handleSelection}>
          {loading ? (
            <Listbox.Loading accessibilityLabel="Loading" />
          ) : (
            optionsMarkup
          )}
        </Listbox>
      </Combobox>
    </div>
  );
};

export default UserSelect;
